import { useRef, useLayoutEffect, useCallback } from 'react'

import { Card, ClickAwayListener } from '@mui/material'
import { createPortal } from 'react-dom'

const DEFAULT_OFFSET = 6
const DEFAULT_MIN_WIDTH = 240
const DEFAULT_MAX_WIDTH = 560
const DEFAULT_Z_INDEX = 1400

const VIEWPORT_GUTTER = 8

function clampWidth(value, min, max) {
  if (Number.isNaN(value)) return min
  return Math.min(Math.max(value, min), max)
}

function resolveExplicitWidth(width, minWidth, maxWidth) {
  if (width == null) return null
  if (typeof width === 'number') {
    const clamped = clampWidth(width, minWidth, maxWidth)
    return `${clamped}px`
  }
  return width
}

function computePosition(anchorEl, offset, minWidth, maxWidth, align, explicitWidth) {
  const rect = anchorEl.getBoundingClientRect()
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : rect.width
  const effectiveMax = Math.min(maxWidth, viewportWidth - VIEWPORT_GUTTER * 2)

  let width
  if (explicitWidth != null) {
    width = explicitWidth
  } else {
    const measured = clampWidth(rect.width, minWidth, effectiveMax)
    width = `${measured}px`
  }

  // Resolve the numeric width up front so every align branch can
  // place the panel relative to that width (left-aligned → panel's
  // left edge sits at the anchor's left edge; right-aligned → panel's
  // right edge sits at the anchor's right edge; center → panel's
  // centre lines up with the anchor's centre). The viewport clamp
  // below then nudges the panel back on-screen if the resolved
  // position would overflow.
  const panelWidth =
    typeof width === 'string' && width.endsWith('px')
      ? parseFloat(width)
      : typeof width === 'number'
        ? width
        : clampWidth(rect.width, minWidth, effectiveMax)

  let left = rect.left
  if (align === 'right') {
    // Subtract the panel width so the panel's right edge aligns with
    // the anchor's right edge. Setting `left = rect.right` (the
    // previous behaviour) put the panel's LEFT edge at the anchor's
    // right edge, pushing the whole panel off-screen right; the
    // viewport clamp then dragged it back to a weird off-centre spot.
    left = rect.right - panelWidth
  } else if (align === 'center') {
    left = rect.left + rect.width / 2 - panelWidth / 2
  }

  // Final viewport clamp: keep the panel inside the visible area no
  // matter how the align branch placed it. Without this the right-
  // aligned panel can overflow the right edge on narrow viewports.
  if (typeof window !== 'undefined') {
    const minLeft = VIEWPORT_GUTTER
    const maxLeft = Math.max(minLeft, window.innerWidth - VIEWPORT_GUTTER - panelWidth)
    left = clampWidth(left, minLeft, maxLeft)
  }

  return {
    position: 'fixed',
    top: `${rect.bottom + offset}px`,
    left: `${left}px`,
    width,
    zIndex: String(DEFAULT_Z_INDEX),
  }
}

export function FloatingPanel({
  open,
  anchorEl,
  anchorRef,
  onClose,
  children,
  offset = DEFAULT_OFFSET,
  minWidth = DEFAULT_MIN_WIDTH,
  maxWidth = DEFAULT_MAX_WIDTH,
  width,
  align = 'left',
  className = '',
  sx,
  cardProps = {},
}) {
  const panelRef = useRef(null)

  const resolveAnchor = useCallback(() => {
    if (anchorEl) return anchorEl
    if (anchorRef && 'current' in anchorRef) return anchorRef.current
    return null
  }, [anchorEl, anchorRef])

  useLayoutEffect(() => {
    if (!open) return undefined
    const explicitWidth = resolveExplicitWidth(width, minWidth, maxWidth)

    const applyStyles = (nextWidth) => {
      const target = resolveAnchor()
      const panel = panelRef.current
      if (!target || !panel) return
      const styles = computePosition(
        target,
        offset,
        minWidth,
        maxWidth,
        align,
        nextWidth ?? explicitWidth
      )
      Object.assign(panel.style, styles)
    }

    const fitToContent = () => {
      if (explicitWidth != null) {
        applyStyles(explicitWidth)
        return
      }
      const target = resolveAnchor()
      if (!target) return
      applyStyles(null)
    }

    fitToContent()

    window.addEventListener('resize', fitToContent)
    window.addEventListener('scroll', fitToContent, true)

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fitToContent) : null
    const target = resolveAnchor()
    const panel = panelRef.current
    if (ro) {
      if (target) ro.observe(target)
      if (panel) ro.observe(panel)
    }

    return () => {
      window.removeEventListener('resize', fitToContent)
      window.removeEventListener('scroll', fitToContent, true)
      if (ro) ro.disconnect()
    }
  }, [open, resolveAnchor, offset, minWidth, maxWidth, width, align])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <ClickAwayListener onClickAway={onClose}>
      <Card
        ref={panelRef}
        elevation={6}
        className={className}
        sx={{ maxWidth: 'calc(100vw - 16px)', borderRadius: '18px', ...sx }}
        {...cardProps}
      >
        {children}
      </Card>
    </ClickAwayListener>,
    document.body
  )
}

export default FloatingPanel
